import { InfoIcon } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface TooltipInfoIconProps {
  description: string;
  ariaLabel: string;
  className?: string;
}

/**
 * Small accessibility-friendly help icon with a hover/focus tooltip.
 *
 * The trigger is a real <button> so keyboard users (Tab + Enter/Space) and
 * screen readers can perceive the tooltip — Radix's `asChild` does not inject
 * focusability into a bare SVG, so wrapping in a button is required.
 */
export function TooltipInfoIcon({
  description,
  ariaLabel,
  className,
}: TooltipInfoIconProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            className={cn(
              'inline-flex cursor-help rounded text-muted-foreground/60 hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              className,
            )}
          >
            <InfoIcon className="h-3 w-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-72">
          {/* pre-line: descriptions may carry \n\n paragraph breaks. */}
          <p className="whitespace-pre-line">{description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
